/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import flow from 'lodash.flow';
import * as React from 'react';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { addToast } from 'src/core/utils/decorators/addToast';
import { addTracker } from 'src/core/utils/decorators/addTracker';
import { getLifetime } from 'src/core/utils/lifetime';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    GeneratedQAsResult,
    GeneratedQuestionsInputDocumentType
} from 'src/languageStudio/appShell/prebuiltDemos/generateQuestionsDemo/generateQuestionsSwagger';
import { commonDemosMessages, resourceEndpointCurlPlaceholder } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { qnaDemoSupportedRegions } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import {
    AnswerQuestionsDemoSamplesJsonOutput,
    GenerateQuestionsTextDemoSamplesJsonOutput,
    GenerateQuestionsURLDemoSamplesJsonOutput
} from 'src/languageStudio/appShell/prebuiltDemos/utils/signedOutMockedSamplesJsonOutput';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { getQuestionsAnsweringService } from 'src/languageStudio/core/services/questionsAnswering/questionsAnsweringService';
import { QuestionAnsweringResult } from 'src/languageStudio/core/services/questionsAnswering/questionsAnsweringSwagger';
import { getTextAnalyticsService } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsService';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export const useQuestionsAnswersGenerator = () => {
    const lifetime = getLifetime();
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { isSignedIn } = rootStore.authStore;
    const textAnalyticsService = getTextAnalyticsService();
    const azureResource = rootStore.azureStore.session.csAccountStore;

    const [answer, setAnswer] = React.useState<QuestionAnsweringResult>();
    const [detectedLanguages, setDetectedLanguages] = React.useState<DetectedLanguagesDocuments>();

    const [qnaAnswer, setQnaAnswer] = React.useState<GeneratedQAsResult>();

    const currentServiceKey = rootStore.azureStore.session.serviceKey;
    const { authorizationHeader } = useAuthKeys({ currentServiceKey, currentResource: azureResource });

    const resetResults = () => {
        tracker.reset();

        if (answer) {
            setAnswer(null);
            setDetectedLanguages(null);
        }

        if (qnaAnswer) {
            setQnaAnswer(null);
        }
    };

    const getGenerateQnACurlCommand = ({
        content,
        language,
        docKind
    }: {
        content: string;
        language: Language['code'];
        docKind: GeneratedQuestionsInputDocumentType;
    }): string => {
        const payload = [
            {
                id: 'documentId',
                content,
                language,
                documentType: docKind
            }
        ];
        const params = {
            fetchRawContent: true,
            maxQACount: LuisConstants.QuestionGeneration.NumberOfGeneratedQAPairsPerPage
        };

        return `ECHO OFF
curl -v -X POST "${azureResource?.endpoint ?? resourceEndpointCurlPlaceholder}language/generate-questionanswers?api-version=${
            LuisConstants.QuestionGeneration.ApiVersion
        }" -H "Content-Type: application/json" -H "${authorizationHeader.key}: ${authorizationHeader.value}" --data-ascii "${JSON.stringify(
            {
                documents: [payload],
                parameters: { params }
            }
        )
            .split('"')
            .join('\\"')}"  `;
    };

    const getCurlCommand = ({
        text,
        language,
        languageAutoDetection
    }: {
        text: string;
        language: Language['code'];
        languageAutoDetection: boolean;
    }): string => {
        const payload = {
            id: 'documentId',
            text: text ?? localize(commonDemosMessages.textCurlPlaceholder),
            language:
                (languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language) ??
                localize(commonDemosMessages.languageCurlPlaceholder)
        };

        return `@ECHO OFF

curl -v -X POST "${azureResource?.endpoint ?? resourceEndpointCurlPlaceholder}language/:query-text?api-version=${
            LuisConstants.QnaMaker.ApiVersion
        }" -H "Content-Type: application/json" -H "Ocp-Apim-Subscription-Key: ${azureResource?.serviceKey ??
            localize(commonDemosMessages.subscriptionKeyCurlPlaceholder)}" --data-ascii "${JSON.stringify({
            records: [payload]
        })
            .split('"')
            .join('\\"')}" `;
    };

    const validate = (language: boolean, input: boolean) => {
        let error: Error;
        const isInvalidInput = !input;
        const isInvalidLanguage = !language;
        const isInvalidResource = qnaDemoSupportedRegions.every(r => r !== azureResource?.region);

        if (isInvalidResource) {
            error = new Error(localize(commonDemosMessages.azureResourceInvalidRegionAndSkuErrorMessage));
            error.name = localize(commonDemosMessages.azureResourceErrorTitle);
        } else if (isInvalidLanguage) {
            error = new Error(localize(commonDemosMessages.languageErrorMessage));
            error.name = localize(commonDemosMessages.languageErrorTitle);
        } else if (isInvalidInput) {
            error = new Error(localize(commonDemosMessages.inputErrorMessage));
            error.name = localize(commonDemosMessages.inputErrorTitle);
        }

        if (isInvalidInput || isInvalidLanguage || isInvalidResource) {
            const inputStep = document.querySelector('#demoPageDocumentInputStep');
            inputStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            throw error;
        }
    };

    const generateAnswer = ({
        document,
        question,
        language,
        sampleTitle,
        autoDetectLanguage
    }: {
        document: string;
        question: string;
        sampleTitle?: string;
        language: Language['code'];
        autoDetectLanguage?: boolean;
    }) =>
        !isSignedIn
            ? flow(() => setAnswer(AnswerQuestionsDemoSamplesJsonOutput[sampleTitle]))()
            : flow(
                  resetResults,
                  async () => {
                      validate(autoDetectLanguage || Boolean(language), Boolean(document) && Boolean(question));

                      let documentLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [{ id: getId(), text: document }]
                          });

                          setDetectedLanguages(languageResult);

                          documentLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      const answers = await getQuestionsAnsweringService().getAnswers({
                          question,
                          language: documentLanguage,
                          records: [{ id: getId(), text: document }]
                      });

                      setAnswer({ answers: answers.answers.sort((a, b) => (a.confidenceScore < b.confidenceScore ? 1 : -1)) });
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    const generateQAs = ({
        document,
        language,
        sampleTitle,
        docKind
    }: {
        document: string;
        language: Language['code'];
        sampleTitle?: string;
        docKind: GeneratedQuestionsInputDocumentType;
    }) =>
        !isSignedIn
            ? flow(() => {
                  setQnaAnswer({
                      generatedQuestions:
                          docKind === 'raw'
                              ? GenerateQuestionsTextDemoSamplesJsonOutput[sampleTitle].value
                              : GenerateQuestionsURLDemoSamplesJsonOutput[sampleTitle].value
                  });
              })()
            : flow(
                  resetResults,
                  async () => {
                      validate(Boolean(language), Boolean(document));

                      await getQuestionsAnsweringService().generateQnAs({
                          language,
                          id: getId(),
                          content: document,
                          documentType: docKind,
                          operationHandle: {
                              timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                              retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                              handleCall: async data => {
                                  if (lifetime.isDisposed) {
                                      return;
                                  }

                                  if (data.status === 'succeeded') {
                                      const generatedQAs = data.result.value;
                                      setQnaAnswer({
                                          generatedQuestions: generatedQAs.sort((a, b) => (a.confidenceScore > b.confidenceScore ? -1 : 1))
                                      });
                                  }

                                  if (data.status === 'failed') {
                                      throw new Error(data.errors?.[0]?.message);
                                  }
                              }
                          }
                      });
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    return {
        answer,
        tracker,
        qnaAnswer,
        detectedLanguages,
        generateQAs,
        getGenerateQnACurlCommand,
        getCurlCommand,
        resetResults,
        generateAnswer
    };
};
